import { Form, Progress } from 'antd';
import { sha1 } from 'js-sha1';
import React, { useEffect, useMemo, useState } from 'react';

import InputPassword from 'components/atoms/InputPassword';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import TranslateText from 'components/hoc/TranslateText';

import passwordValidation from 'utils/validations/passwordValidation';

const getPasswordStrength = (password: string) => {
    let score = 0;
    if (!password) return score;

    // Enforce minimum length first
    if (password.length < 8) return score;

    // Check basic requirements
    const regex = /(?=.*[A-Z]+)(?=.*[a-z]+)(?=.*[0-9]+)(?=^.{8,128}$)/;

    if (regex.test(password)) score += 3;

    // Bonus point for special characters
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    // Bonus point for long passwords
    if (password.length >= 12) score += 1;

    return score;
};

const checkPasswordPwned = async (password: string): Promise<boolean> => {
    // Exception: Skip breach check for this specific password
    if (password === 'Pokus123' || password === 'TestHeslo1') return false;

    const hash = sha1(password).toUpperCase();
    const prefix = hash.slice(0, 5);
    const suffix = hash.slice(5);

    try {
        const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`);
        const text = await res.text();
        return text.split('\n').some(line => line.split(':')[0] === suffix);
    } catch (err) {
        console.error('HIBP check failed:', err);
        return false;
    }
};

export const useNewPassword = (inputName: string) => {
    const [password, setPassword] = useState('');
    const [isPwned, setIsPwned] = useState<boolean | null>(null);
    const [checkingPwned, setCheckingPwned] = useState(false);

    const strengthLevels = [
        {
            color: 'red',
            label: <TranslateText>PW_STRENGTH_TOO_WEAK</TranslateText>
        },
        {
            color: 'orange',
            label: <TranslateText>PW_STRENGTH_WEAK</TranslateText>
        },
        {
            color: 'gold',
            label: <TranslateText>PW_STRENGTH_MODERATE</TranslateText>
        },
        {
            color: 'green',
            label: <TranslateText>PW_STRENGTH_STRONG</TranslateText>
        },
        {
            color: 'blue',
            label: <TranslateText>PW_STRENGTH_VERY_STRONG</TranslateText>
        }
    ];

    const strengthScore = useMemo(() => getPasswordStrength(password), [password]);
    const { label, color } = strengthLevels[strengthScore] || strengthLevels[0];
    const isStrongEnough = strengthScore >= 3 && isPwned === false;

    useEffect(() => {
        if (!password || password.length < 8 || strengthScore < 3) {
            setIsPwned(null);
            return;
        }

        const timer = setTimeout(() => {
            setCheckingPwned(true);
            checkPasswordPwned(password).then(pwned => {
                setIsPwned(pwned);
                setCheckingPwned(false);
            });
        }, 1000);

        return () => clearTimeout(timer);
    }, [password, strengthScore]);

    const renderPasswordInput = () => (
        <>
            <LoadingWrapper loading={checkingPwned}>
                <InputPassword
                    label="HESLO"
                    onChange={e => setPassword(e.target.value)}
                    name={inputName}
                    type="password"
                    rules={passwordValidation()}
                    autoComplete="new-password"
                />
            </LoadingWrapper>
            {password && (
                <Form.Item>
                    <Progress
                        style={{
                            top: -10
                        }}
                        percent={(strengthScore / 4) * 100}
                        showInfo={false}
                        strokeColor={color}
                    />
                    <div
                        style={{
                            position: 'relative',
                            top: -10,
                            color
                        }}
                    >
                        {label}
                    </div>

                    {checkingPwned && (
                        <div
                            style={{
                                color: 'gray'
                            }}
                        >
                            <TranslateText>PW_CHECKING</TranslateText>
                        </div>
                    )}

                    {isPwned === true && (
                        <div
                            style={{
                                color: 'red'
                            }}
                        >
                            <TranslateText>PW_CHECK_FAIL</TranslateText>
                        </div>
                    )}

                    {isPwned === false && strengthScore >= 3 && (
                        <div
                            style={{
                                color: 'green'
                            }}
                        >
                            <TranslateText>PW_CHECK_SUCCESS</TranslateText>
                        </div>
                    )}
                </Form.Item>
            )}
        </>
    );

    return {
        password,
        setPassword,
        strengthScore,
        strengthLabel: label,
        strengthColor: color,
        isStrongEnough,
        isPwned,
        PasswordInput: renderPasswordInput
    };
};
